import { Component, inject, OnDestroy, OnInit, output } from '@angular/core';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { Microsoft360ViewContext } from '../../../../shared/contexts/microsoft-360-view.context';
import { AccountType, Microsoft360View, ResourceAccountType } from '../../../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Microsoft360ViewPollingService } from '../../../../shared/services/microsoft/microsoft-360-view-polling.service';

interface Ms360ViewBadge {
  badgeText?: string;
  outerSpanClass?: string;
  innerSpanClass?: string;
  iconClass?: ZiroIcons | string;
  tooltip?: string;
  tooltipClass?: string;
  tooltipPlacement?: string;
}

@Component({
  selector: 'ziro-microsoft-360-detail-card',
  templateUrl: './microsoft360-detail-card.component.html',
  styleUrls: ['./microsoft360-detail-card.component.scss'],
  standalone: false,
  providers: [Microsoft360ViewPollingService],
})
export class Microsoft360DetailCardComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private microsoft360ViewContext = inject(Microsoft360ViewContext);
  private translateService = inject(TranslateService);
  private _subscriptions = new Subscription();

  readonly viewLoaded = output<boolean>();
  ziroIcons = ZiroIcons;
  microsoft360View: Microsoft360View;
  isPendingUsageLocation = false;
  usageLocationPendingMsg = this.translateService.instant('tkey;microsoft_360.view.contact.card.usage.pending.message');
  userPhoto: string;
  isResourceAccount: boolean;
  badge: Ms360ViewBadge;
  resourceAccountBadge: Ms360ViewBadge;
  accountType: AccountType;
  isAccountLocked: boolean;
  upn: string;
  tenantId: string;

  ngOnInit(): void {
    this.upn = this.route.snapshot.paramMap.get('upn');
    this.tenantId = this.route.snapshot.paramMap.get('tenantId');

    const ms360ViewSub = this.microsoft360ViewContext.state$.subscribe((ms360View: Microsoft360View) => {
      this.microsoft360View = ms360View;
      this.isPendingUsageLocation = ms360View.licensingStatus.usageLocationUpdateInProgress;
      this.accountType = this.microsoft360View.accountType;
      this.isResourceAccount = this.accountType === AccountType.RESOURCE_ACCOUNT;
      this.isAccountLocked = !ms360View.microsoft365UserLicenses.accountEnabled;

      this.userPhoto = ms360View.photoBase64 ? `data:image/png;base64,${ms360View.photoBase64}` : null;
      if (this.microsoft360View?.microsoft365UserLicenses?.userPrincipalName === this.upn) {
        this.viewLoaded.emit(true);
      }

      this.badge = this._getBadge();
      if (this.isResourceAccount) {
        this.resourceAccountBadge = this._getResourceAccountBadge();
      }
    });

    this._subscriptions.add(ms360ViewSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  private _getBadge(): Ms360ViewBadge {
    let badge: Ms360ViewBadge;

    if (this.accountType === AccountType.RESOURCE_ACCOUNT) {
      badge = {
        badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account',
        outerSpanClass: 'badge bg-secondary',
        tooltip: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.tooltip',
        tooltipClass: 'tooltip--white badge-tooltip',
        tooltipPlacement: 'top',
      };
    } else if (this.isAccountLocked) {
      badge = {
        badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.account_locked',
        outerSpanClass: 'badge bg-danger',
        innerSpanClass: 'ms-1',
        iconClass: this.ziroIcons.CIRCLE_EXCLAMATION,
        tooltip: 'tkey;microsoft_360.view.contact.card.user.badge.account_locked.tooltip',
        tooltipClass: 'tooltip--white badge-tooltip',
        tooltipPlacement: 'top',
      };
    } else if (this.microsoft360View.onPremiseUserPrincipalName) {
      badge = {
        badgeText: this.translateService.instant('tkey;microsoft_360.view.contact.card.user.badge.on_premise_upn'),
        outerSpanClass: 'badge bg-secondary',
        innerSpanClass: 'ms-1',
        iconClass: 'fa-brands icon-windows-logo',
      };
      if (this.upn !== this.microsoft360View.onPremiseUserPrincipalName) {
        badge = {
          ...badge,
          tooltip: this.translateService.instant(
            'tkey;microsoft_360.view.contact.card.user.badge.on_premise_upn.tooltip',
            {
              onPremUpn: this.microsoft360View.onPremiseUserPrincipalName,
            },
          ),
          tooltipClass: 'tooltip--white badge-tooltip',
          tooltipPlacement: 'top',
        };
      }
    }
    return badge;
  }

  private _getResourceAccountBadge(): Ms360ViewBadge {
    let badge: Ms360ViewBadge;

    if (this.microsoft360View.resourceAccountDetails) {
      if (this.microsoft360View.resourceAccountDetails.type === ResourceAccountType.CALL_QUEUE) {
        badge = {
          badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.call_queue',
          outerSpanClass: 'badge bg-secondary',
          tooltip: this.translateService.instant(
            'tkey;microsoft_360.view.contact.card.user.badge.resource.account.tooltip.type',
            {
              resourceAcctType: this.translateService.instant(
                'tkey;microsoft_360.view.contact.card.user.badge.resource.account.call_queue',
              ),
            },
          ),
          tooltipClass: 'tooltip--white badge-tooltip',
          tooltipPlacement: 'top',
          iconClass: ZiroIcons.CALL_QUEUE,
        };
      } else if (this.microsoft360View.resourceAccountDetails.type === ResourceAccountType.AUTO_ATTENDANT) {
        badge = {
          badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.auto_attendant',
          outerSpanClass: 'badge bg-secondary',
          tooltip: this.translateService.instant(
            'tkey;microsoft_360.view.contact.card.user.badge.resource.account.tooltip.type',
            {
              resourceAcctType: this.translateService.instant(
                'tkey;microsoft_360.view.contact.card.user.badge.resource.account.auto_attendant',
              ),
            },
          ),
          tooltipClass: 'tooltip--white badge-tooltip',
          tooltipPlacement: 'top',
          iconClass: ZiroIcons.AUTO_ATTENDANT,
        };
      } else if (this.microsoft360View.resourceAccountDetails.type === ResourceAccountType.NOT_SUPPORTED) {
        badge = {
          badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.not_supported',
          outerSpanClass: 'badge bg-secondary',
          tooltip: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.tooltip.unsupported',
          tooltipClass: 'tooltip--white badge-tooltip',
          tooltipPlacement: 'top',
          iconClass: ZiroIcons.DISABLED,
        };
      } else {
        badge = {
          badgeText: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.undefined',
          outerSpanClass: 'badge bg-secondary',
          tooltip: 'tkey;microsoft_360.view.contact.card.user.badge.resource.account.tooltip.unknown',
          tooltipClass: 'tooltip--white badge-tooltip',
          tooltipPlacement: 'top',
          iconClass: ZiroIcons.DISABLED,
        };
      }
    }

    return badge;
  }
}
